import { AxiosResponse } from 'axios';
import { endOfMonth, format, startOfMonth } from 'date-fns';

import { CandidateLifecycleStatuses, DateFormats, LifecycleStatuses, REQUEST_WITHOUT_EXECUTOR } from '@constants';
import {
  CandidateStatusListItem,
  RequestEmployee,
  RequestFilterValuesResponse,
  SelectOption,
} from '@innowise-group/core';
import i18n from '@localization';

import { getDictionaryValueTitle, getNamedValueTitle, localizeRequestEmployee } from '../requests/requests.mappers';

import {
  CandidateFilterOptions,
  DateSegments,
  FiltersEntity,
  FiltersState,
  MoneyFilterType,
  NestedSelectFilterType,
  RequestListFilter,
  SelectFilterType,
  Tabs,
} from './filters.types';

export const statusesMapper = ({
  data,
  ...response
}: AxiosResponse<CandidateStatusListItem[]>): AxiosResponse<SelectOption[]> => ({
  ...response,
  data: data.map(({ id: value, localizedName: title }) => ({ value, title })),
});

export const citiesOptionsFilter = (state: FiltersState) => {
  const selectedCountriesInFilter = Object.values(state.candidate.countries.state).reduce((acc, { active, value }) => {
    return active ? [...acc, value] : acc;
  }, []);
  const filteredCitiesOptions = state.candidateInitialOptions.citiesOptions?.filter((el) =>
    selectedCountriesInFilter.length ? selectedCountriesInFilter.some((countryId) => countryId === el.parent) : true,
  );
  return selectFilterMapper(state.candidate.cities, filteredCitiesOptions, false);
};

export const selectFilterMapper = (initialData: SelectFilterType, options: SelectOption[], reset?: boolean) => {
  return {
    ...initialData,
    state: {
      ...(Array.isArray(options) &&
        Object.entries(options).reduce((acc, [, val]) => {
          return {
            ...acc,
            [val.value]: {
              ...initialData.state?.[val.value],
              ...val,
              ...(reset && { active: !reset }),
            },
          };
        }, {})),
    },
  };
};

// A field of a request replaces the selection of its filter: the filter is added when some value is in its options and
// dropped otherwise. Values missing from the options are skipped
export const selectFilterValuesMapper = <T extends SelectFilterType>(filter: T, values: string[]): T => ({
  ...filter,
  active: !!filter.pinned || values.some((value) => filter.state[value]),
  isOpen: false,
  state: Object.entries(filter.state).reduce<SelectFilterType['state']>((acc, [key, item]) => {
    acc[key] = { ...item, active: values.includes(key) };
    return acc;
  }, {}),
});

// The same for a filter with nested values (grades, language levels): `nested` lists the nested values to select
export const nestedSelectFilterValuesMapper = (
  filter: NestedSelectFilterType,
  values: { value: string; nested: string[] }[],
): NestedSelectFilterType => {
  const nestedByValue = new Map(values.map(({ value, nested }) => [value, nested]));

  return {
    ...filter,
    active: !!filter.pinned || values.some(({ value }) => filter.state[value]),
    isOpen: false,
    state: Object.entries(filter.state).reduce<NestedSelectFilterType['state']>((acc, [key, item]) => {
      const nested = nestedByValue.get(key);
      acc[key] = {
        ...item,
        active: !!nested,
        ...(item.nested && {
          nested: Object.entries(item.nested).reduce<NestedSelectFilterType['state'][string]['nested']>(
            (nestedAcc, [nestedKey, nestedItem]) => {
              nestedAcc[nestedKey] = { ...nestedItem, active: !!nested?.includes(nestedKey) };
              return nestedAcc;
            },
            {},
          ),
        }),
      };
      return acc;
    }, {}),
  };
};

export const candidateStatusFiltersMapper = (
  initialData: NestedSelectFilterType,
  options: CandidateFilterOptions['statusOptions'],
  reset?: boolean,
) => {
  return {
    ...initialData,
    state: {
      ...initialData.state,
      ...Object.entries(options).reduce((acc, [key, val]) => {
        return {
          ...acc,
          [key]: {
            ...(initialData.state[key]
              ? {
                  ...val,
                  ...initialData.state[key],
                  ...(reset && { active: !reset }),
                  nested: initialData.state[key].nested
                    ? {
                        ...Object.entries(val.nested).reduce((ac, [k, v]) => {
                          return {
                            ...ac,
                            [k]: {
                              ...v,
                              ...initialData.state[key].nested[k],
                              ...(reset && { active: !reset }),
                            },
                          };
                        }, {}),
                      }
                    : val.nested,
                }
              : {
                  ...val,
                  active: false,
                }),
          },
        };
      }, {}),
    },
  };
};

export const nestedSelectFilterMapper = (
  initialData: NestedSelectFilterType,
  mainOptions: SelectOption[],
  nestedOptions: SelectOption[],
  reset?: boolean,
) => {
  return {
    ...initialData,
    state: {
      ...initialData.state,
      ...mainOptions.reduce((acc, option) => {
        const { nested: previousNested, ...previousParentState } = initialData.state?.[option.value] || {};
        const nested = nestedOptions
          .filter((nestedOption) => !nestedOption.parent || nestedOption.parent === option.value)
          .reduce(
            (nestedAcc, nestedOption) => ({
              ...nestedAcc,
              [nestedOption.value]: {
                ...nestedOption,
                ...previousNested?.[nestedOption.value],
                ...(reset && { active: !reset }),
              },
            }),
            {},
          );

        return {
          ...acc,
          [option.value]: {
            ...option,
            ...previousParentState,
            ...(reset && { active: !reset }),
            ...(Object.keys(nested).length && { nested }),
          },
        };
      }, {}),
    },
  };
};

export const moneyFilterMapper = (initialData: MoneyFilterType, options: SelectOption[], reset?: boolean) => {
  return {
    ...initialData,
    state: {
      ...initialData.state,
      ...(options &&
        options?.reduce(
          (acc, el, index) => ({
            ...acc,
            currencies: [...acc.currencies, el],
            ...(reset && index === 0 && { currency: el.value }),
          }),
          { currencies: [], currency: initialData.state.currency || options[0].value },
        )),
    },
  };
};

export const serializeSelectData = (key: string, filters: FiltersEntity[keyof FiltersEntity]) => {
  return (
    filters?.[key]?.active && {
      [key]: Object.entries<{ active: boolean }>(filters?.[key]?.state)?.reduce((acc, [key, { active }]) => {
        return active ? [...acc, key] : acc;
      }, []),
    }
  );
};

export const serializeCandidateWorkFormatsData = (
  key: string,
  filters: FiltersEntity[keyof FiltersEntity],
  options: CandidateFilterOptions,
) => {
  return (
    filters?.[key]?.active && {
      workFormats: Object.entries<{ active: boolean }>(filters?.[key]?.state)?.reduce((acc, [key, { active }]) => {
        const isWorkFormatsOption = options.workFormatsOptions.some((el) => el.value === key);
        return active && isWorkFormatsOption ? [...acc, key] : acc;
      }, []),
      workLoads: Object.entries<{ active: boolean }>(filters?.[key]?.state)?.reduce((acc, [key, { active }]) => {
        const isWorkLoadsOption = options.workloadsOptions.some((el) => el.value === key);
        return active && isWorkLoadsOption ? [...acc, key] : acc;
      }, []),
      employmentForms: Object.entries<{ active: boolean }>(filters?.[key]?.state)?.reduce((acc, [key, { active }]) => {
        const isEmploymentFormsOption = options.employmentFormsOptions.some((el) => el.value === key);
        return active && isEmploymentFormsOption ? [...acc, key] : acc;
      }, []),
    }
  );
};

export const serializerBooleanData = (key: string, filters: FiltersEntity[keyof FiltersEntity]) => {
  return filters?.[key]?.active && { [key]: filters?.[key]?.state?.checked };
};

export const candidateStringDataSerializer = (
  key: string,
  field: string,
  filters: FiltersEntity[keyof FiltersEntity],
) => {
  return filters?.[key] && { [field]: filters?.[key]?.state?.value };
};

export const lifecycleStatusSerializer = (key: string, tab: FiltersState['currentTab']) => {
  if (tab === Tabs.Archive) return { [key]: LifecycleStatuses.Deleted };
  return { [key]: LifecycleStatuses.Actual };
};

export const candidateLifecycleStatusSerializer = (key: string, tab: FiltersState['currentTab']) => {
  if (tab === Tabs.Archive) return { [key]: [CandidateLifecycleStatuses.Deleted, CandidateLifecycleStatuses.Merged] };
  return { [key]: [LifecycleStatuses.Actual] };
};

export const claimsToApproveStatusSerializer = (key: string, tab: FiltersState['currentTab']) => {
  if (tab === Tabs.UnderApproval) return { [key]: true };
  return { [key]: false };
};

export const candidateResponsibleEmployeeDataSerializer = (
  key: string,
  responsible: FiltersState['responsible'],
  tab: FiltersState['currentTab'],
) => {
  if (tab === Tabs.My) {
    return { [key]: [responsible] };
  }
  return { [key]: [] };
};

export const serializerDateRangeData = (
  min: string,
  max: string,
  key: string,
  filters: FiltersEntity[keyof FiltersEntity],
  formatter?: (date: string | Date | number) => string | Date | number,
) => {
  return {
    ...(filters?.[key]?.active && {
      [min]: typeof formatter === 'function' ? formatter(filters?.[key]?.state?.from) : filters?.[key]?.state?.from,
      [max]: typeof formatter === 'function' ? formatter(filters?.[key]?.state?.to) : filters?.[key]?.state?.to,
    }),
  };
};

export const serializerNumberRangeData = (
  min: string,
  max: string,
  key: string,
  filters: FiltersEntity[keyof FiltersEntity],
) => {
  return {
    ...(filters?.[key]?.active && {
      [min]: filters?.[key]?.state?.from * 12,
      [max]: filters?.[key]?.state?.to * 12,
    }),
  };
};

export const serializerAgeRangeData = (
  min: string,
  max: string,
  key: string,
  filters: FiltersEntity[keyof FiltersEntity],
) => {
  return {
    ...(filters?.[key]?.active && {
      [min]: filters?.[key]?.state?.from,
      [max]: filters?.[key]?.state?.to,
    }),
  };
};

export const serializeNestedSelectData = (
  key: string,
  parentEntityName: string,
  childEntityName: string,
  filters: FiltersEntity[keyof FiltersEntity],
  commentEntityName?: string,
) => {
  const filterValues = filters?.[key] && {
    [key]: Object.entries<NestedSelectFilterType['state'][string]>(filters?.[key]?.state)?.reduce(
      (acc, [, { active, value, nested, comment }]) => {
        return active
          ? [
              ...acc,
              {
                [parentEntityName]: value,
                ...(commentEntityName && { [commentEntityName]: comment || '' }),
                [childEntityName]: nested
                  ? Object.entries(nested)?.reduce((acc, [, { active, value }]) => {
                      return active ? [...acc, value] : acc;
                    }, [])
                  : [],
              },
            ]
          : acc;
      },
      [],
    ),
  };
  if (filterValues?.[key]?.length) return filterValues;
};

export const serializerMoneyData = (
  key: string,
  min: string,
  max: string,
  filters: FiltersEntity[keyof FiltersEntity],
) => {
  return (
    filters?.[key]?.active && {
      salaryFilter: {
        [min]: filters?.[key]?.state?.from,
        [max]: filters?.[key]?.state?.to,
        currency: filters?.[key]?.state?.currency,
      },
    }
  );
};

export const serializerPlaceDataFilter = (key: string, filters: FiltersEntity[keyof FiltersEntity]) => {
  return {
    ...(filters?.[key]?.active && {
      [key]: {
        place: filters?.[key]?.state?.place,
        profession: filters?.[key]?.state?.profession,
        from: filters?.[key]?.state?.from,
        to: filters?.[key]?.state?.to,
      },
    }),
  };
};

export const serializeRequestWorkLoads = (key: string, filters: FiltersEntity[keyof FiltersEntity]) => {
  return (
    serializeSelectData(key, filters)?.[key] && {
      [key]: serializeSelectData(key, filters)?.[key],
    }
  );
};

export const serializeSingleSelectOptionUnit = (key: string, filters: FiltersEntity[keyof FiltersEntity]) => {
  return (
    filters?.[key]?.active && {
      [key]: Object.entries<{ active: boolean }>(filters?.[key]?.state)?.reduce((acc, [key, { active }]) => {
        return active ? key : acc;
      }, ''),
    }
  );
};

export const serializeOverdueData = (key: string, filters: FiltersEntity[keyof FiltersEntity]) => {
  const isSame = !!filters?.[key]?.state?.OVERDUE?.active === !!filters?.[key]?.state?.VALID?.active;
  const value = isSame ? null : !!filters?.[key]?.state?.OVERDUE?.active;

  return filters?.[key]?.active && { isOverdue: value };
};

export const serializeRequestAuthorEmploye = (
  key: string,
  targetFieldName: string,
  filters: FiltersEntity[keyof FiltersEntity],
) => {
  if (filters?.[key]?.state?.length) {
    return {
      [targetFieldName]: filters?.[key]?.state,
    };
  }
};

export const serializeBooleanList = (key: string, filters: FiltersEntity[keyof FiltersEntity]) => {
  return filters?.[key]?.active && { [key]: [filters?.[key]?.state?.checked] };
};

export const serializerDateValue = (key: string, filters: FiltersEntity[keyof FiltersEntity]) => {
  return {
    ...(filters?.[key]?.active && {
      [key]: filters?.[key]?.state,
    }),
  };
};

export const serializeSelectNumberData = (key: string, filters: FiltersEntity[keyof FiltersEntity]) => {
  return (
    filters?.[key]?.active && {
      [key]: Object.entries<{ active: boolean }>(filters?.[key]?.state)?.reduce((acc, [key, { active }]) => {
        return active ? [...acc, Number(key)] : acc;
      }, []),
    }
  );
};

// «My requests» are the ones the current employee is a customer of; a selection in the customers filter wins
export const serializeRequestCustomerIds = (
  key: string,
  responsible: FiltersState['responsible'],
  tab: FiltersState['currentTab'],
  filters: FiltersEntity[keyof FiltersEntity],
) => {
  const selected = serializeSelectNumberData(key, filters);
  if (tab === Tabs.My && responsible && !selected?.[key]?.length) {
    return { [key]: [responsible] };
  }
  return selected;
};

// The «without executor» option is sent as a null id
export const serializeRequestExecutorIds = (key: string, filters: FiltersEntity[keyof FiltersEntity]) => {
  const selected = serializeSelectData(key, filters);
  return (
    selected && {
      [key]: selected[key].map((id: string) => (id === REQUEST_WITHOUT_EXECUTOR ? null : Number(id))),
    }
  );
};

// An empty search bar is not sent: the backend would match it against every request
export const serializeSearchValue = (key: string, field: string, filters: FiltersEntity[keyof FiltersEntity]) => {
  const value = filters?.[key]?.state?.value?.trim();
  return value && { [field]: value };
};

// The segment filter picks a month and the bounds are inclusive: «exact» sends the first and the last day of the
// month, «since» its first day, «until» its last day
export const serializeDateSegmentRange = (
  from: string,
  to: string,
  key: string,
  filters: FiltersEntity[keyof FiltersEntity],
) => {
  const state = filters?.[key]?.state;
  if (!filters?.[key]?.active || !state?.date) return {};

  const month = new Date(state.date);
  return {
    ...(state.currentSegment !== DateSegments.Until && {
      [from]: format(startOfMonth(month), DateFormats.YearFirst),
    }),
    ...(state.currentSegment !== DateSegments.Since && { [to]: format(endOfMonth(month), DateFormats.YearFirst) }),
  };
};

// An empty bound of a range is left out: `new Date(null)` would turn it into 1970-01-01
export const formatFilterDate = (date?: string | Date | number | null): string | undefined =>
  date ? format(new Date(date), DateFormats.YearFirst) : undefined;

const mapRequestEmployeeToOption = (employee: RequestEmployee): SelectOption => ({
  value: employee.id.toString(),
  title: localizeRequestEmployee(employee).localizedFullName,
});

export const requestFilterValuesMapper = (
  filterName: RequestListFilter,
  data: RequestFilterValuesResponse,
): SelectOption[] => {
  switch (filterName) {
    case 'customerIds':
      return (data.customerIds || []).map(mapRequestEmployeeToOption);
    case 'executorIds':
      // The backend puts a null executor first when some requests have no executors
      return (data.executorIds || []).map((item) =>
        item
          ? mapRequestEmployeeToOption(item)
          : { value: REQUEST_WITHOUT_EXECUTOR, title: i18n.t('filters.withoutExecutor') },
      );
    case 'unitIds':
    case 'divisionIds':
      return (data[filterName] || []).map((item) => ({
        value: item.id.toString(),
        title: getNamedValueTitle({ id: item.id, name: item.orgUnitName }),
      }));
    case 'countryIds':
      return (data.countryIds || []).map((item) => ({ value: item.id.toString(), title: getNamedValueTitle(item) }));
    case 'cityIds':
      return (data.cityIds || []).map((item) => ({
        value: item.id.toString(),
        title: getNamedValueTitle(item),
        parent: item.countryId?.toString(),
      }));
    default:
      return (data[filterName] || []).map((item) => ({ value: item.valueId, title: getDictionaryValueTitle(item) }));
  }
};
